import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ClipboardList, Clock, ListChecks, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ILLUSTRATION = 'https://images.unsplash.com/photo-1523705480679-b5d0cc17a656?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHwzZCUyMGNsaXBib2FyZCUyMHN1cnZleSUyMGlsbHVzdHJhdGlvbnxlbnwwfHx8fDE3ODI5MDAyMjh8MA&ixlib=rb-4.1.0&q=85';

/**
 * MandatorySurveyPopup — large, engaging modal for pending surveys.
 * Never opens on a /surveys/{id}/fill route; skips persist in sessionStorage.
 */
export default function MandatorySurveyPopup({ onResolved }) {
  const { sessionToken, getAuthHeader, employee } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const onFillRoute = /^\/surveys\/[^/]+\/fill/.test(location.pathname);
  // Skip is per-login (keyed by session token) so the popup dismisses for the current
  // session but re-appears on the next login until the survey is actually submitted.
  const skipKey = `survey-skipped:${sessionToken || 'anon'}`;

  const getSkipped = useCallback(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(skipKey) || '[]')); }
    catch { return new Set(); }
  }, [skipKey]);

  const addSkipped = useCallback((id) => {
    const s = getSkipped(); s.add(id);
    try { sessionStorage.setItem(skipKey, JSON.stringify([...s])); } catch { /* ignore */ }
  }, [getSkipped, skipKey]);

  useEffect(() => {
    if (!sessionToken || !employee) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/surveys/mine`, { headers: getAuthHeader() });
        if (!r.ok || cancelled) return;
        setPending(await r.json());
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, employee?.employee_id]);

  useEffect(() => {
    if (onFillRoute) { setCurrent(null); return; }
    const skipped = getSkipped();
    let completed = new Set();
    try { completed = new Set(JSON.parse(localStorage.getItem(`survey-completed:${employee?.employee_id || 'anon'}`) || '[]')); } catch { /* ignore */ }
    const first = pending.find(s => !skipped.has(s.survey_id) && !completed.has(s.survey_id));
    setCurrent(first || null);
  }, [pending, onFillRoute, getSkipped, employee]);

  // Signal the survey phase is resolved (nothing to show or all dismissed) so the
  // announcements popup can appear afterwards.
  useEffect(() => {
    if (loaded && !current) onResolved?.();
  }, [loaded, current, onResolved]);

  const fill = () => {
    if (!current) return;
    const id = current.survey_id;
    setCurrent(null);
    navigate(`/surveys/${id}/fill`);
  };

  const skip = () => {
    if (!current) return;
    const id = current.survey_id;
    addSkipped(id); // dismiss for this login session only (re-appears next login until submitted)
    setPending(prev => prev.filter(s => s.survey_id !== id));
    setCurrent(null);
  };

  if (!current || onFillRoute) return null;

  const qCount = current.question_count || 0;
  const estMin = Math.max(1, Math.ceil((qCount * 25) / 60)); // ~25s per question
  const accent = current.theme_color || '#E60000';
  const logoSrc = current.logo_url ? `${BACKEND_URL}/api/public/survey-logo/${current.survey_id}` : null;

  return (
    <Dialog open={!!current} onOpenChange={(open) => !open && skip()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0" data-testid="mandatory-survey-popup">
        <DialogTitle className="sr-only">You have a pending feedback form</DialogTitle>
        <div className="grid md:grid-cols-5">
          {/* Illustration / logo side */}
          <div className="md:col-span-2 relative hidden md:flex items-center justify-center p-6"
            style={{ background: `linear-gradient(160deg, ${accent}14, ${accent}33)` }}>
            <motion.img
              src={logoSrc || ILLUSTRATION} alt="Feedback" data-testid="popup-logo"
              className={logoSrc ? 'max-w-[80%] max-h-40 object-contain drop-shadow-xl' : 'w-full h-44 object-contain drop-shadow-xl'}
              initial={{ scale: 0.85, opacity: 0, rotate: logoSrc ? 0 : -4 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 120, damping: 14 }}
            />
            <div className="absolute top-4 left-4 flex items-center gap-2 text-xs font-bold tracking-wide"
              style={{ color: accent }}>
              <ClipboardList className="h-4 w-4" /> FEEDBACK
            </div>
          </div>

          {/* Content side */}
          <div className="md:col-span-3 p-7 space-y-4">
            <div>
              <p className="text-xs font-bold tracking-[0.1em] uppercase text-muted-foreground">We'd love your feedback</p>
              <h2 className="text-2xl font-bold tracking-tight leading-snug mt-1">{current.title}</h2>
              {current.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{current.description}</p>}
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-muted/40">
                <ListChecks className="h-4 w-4" style={{ color: accent }} />
                <span><b>{qCount}</b> question{qCount === 1 ? '' : 's'}</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-muted/40">
                <Clock className="h-4 w-4" style={{ color: accent }} />
                <span>~<b>{estMin}</b> min</span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Takes just a moment to share your feedback.
              {current.expires_at && <> · Closes on <b>{current.expires_at.slice(0, 10)}</b></>}
            </p>

            <div className="flex items-center justify-end gap-2 pt-1">
              <motion.div whileTap={{ scale: 0.97 }}>
                <Button variant="ghost" onClick={skip} data-testid="popup-skip-btn">Skip for now</Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button onClick={fill} data-testid="popup-fill-btn"
                  className="text-white shadow-lg" style={{ backgroundColor: accent }}>
                  Give Feedback <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
